import React, { useEffect, useState, useRef } from 'react';
import { userStore } from '../../store';
import cx from 'classnames';
import deleteIconPath from '@icons/trash.svg';
import duplicateIconPath from '@icons/duplicate.svg';
import { useNavigate } from 'react-router';

import PlaylistGroupPlylists from '../playlistGroupPlylists/PlaylistGroupPlylists';
import GoBackButton from '../goBackButton/GoBackButton';
import DeviceAssignmentModal from '../deviceAssignmentModal/DeviceAssignmentModal';

import { useTranslation } from 'react-i18next';
import { playlistStore, generalStore } from '../../store';
import { apiDelete, apiPatch, apiPost } from '../../js/api';
import Server from '../../js/config/_server';
import { RESPONSE, ROUTES } from '../../js/_constants';
import DeleteConfirmationModal from '../deleteConfirmationModal/DeleteConfirmationModal';
import EditPlaylistGroup from './EditPlaylistGroup';
import {
    type Device,
    type Dealership,
    type PlaylistGroup, Country
} from '../../api/types/index';
import { type ResourceResponse } from '../../api/types/response';
import IconButton from '../iconButton/IconButton';
import assignIconPath from '@icons/sync-small.svg';
import api from '../../api';
import { isNotEmpty, getIri } from '../../js/fpUtils';
import Loader from '../loader/Loader';
import Modal from '../modal/Modal';
import AssignPlaylistToPlaylistGroupOverlay from '../assignPlaylistToPlaylistGroupOverlay/AssignPlaylistToPlaylistGroupOverlay';
import Button from '../button/Button';
import Accordion from '../accordion/Accordion';

import './PlaylistGroupContent.scss';
import {allCountries} from "../../js/countries";
import CreatePlaylistGroupModal from "../createPlaylistGroupModal/CreatePlaylistGroupModal";

type PlaylistContentProps = {
    playlistGroupId: string;
    playlistGroup: PlaylistGroup;
};

export const PlaylistGroupContent = ({
    playlistGroupId,
    playlistGroup,
}: PlaylistContentProps) => {
    const navigate = useNavigate();

    const { t } = useTranslation();
    const { details } = userStore();
    const { getPlaylistGroup } = playlistStore();
    const { refreshCoreData, setPageName } = generalStore();

    const [isEditingPlaylistGroup, setIsEditingPlaylistGroup] = useState(false);
    // Market responsible & Brand responsible should be able to edit all playlists within their country(s)
    // Dealers should only be able to edit own playlists
    const [isEditable, setIsEditable] = useState(false);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
    const [isAddPlaylistModalOpen, setIsAddPlaylistModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [selectedSource, setSelectedSource] = useState('my_playlists');

    const [isSaving, setIsSaving] = useState(false);
    const [isFetchingDevicesInfo, setIsFetchingDevicesInfo] = useState(true);
    const [isDeletingPlaylistGroup, setIsDeletingPlaylistGroup] = useState(false);
    const [playlistGroupInfo, setPlaylistGroupInfo] = useState<PlaylistGroup>(playlistGroup);

    /**
     * We only want to display dealerships of the playlist which are also assigned to user.
     * Thus, we use /users/me/dealerships endpoint for this purpose.
     * We take playlist's dealership ids and sent these to API.
     * Then using the received values.
     * If the response array is empty, it means that none of the playlist's dealerships are assigned to user
     */

    const [devicesPersisted, setDevicesPersisted] = useState<Device[]>([]);

    const [devicesToSet, setDevicesToSet] = useState<Device[]>([]);

    const [showDevicesHint, setShowDevicesHint] = useState(false);

    const controllerRef = useRef<AbortController | null>(null);

    const handleEditPlaylistGroup = () => setIsEditingPlaylistGroup(true);

    const handleCloseEditModal = () => setIsEditingPlaylistGroup(false);

    const handleModifyDevices = () => setIsDeviceModalOpen(true);

    const handleDeleteModalOpen = () => setIsDeleteModalOpen(true);

    const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

    const handleOpenAddPlaylistModal = () => setIsAddPlaylistModalOpen(true);

    const handleCloseAddPlaylistModal = () => setIsAddPlaylistModalOpen(false);

    const onPlaylistAssignment = async(playlistIri) => {

        setIsSaving(true);

        const existedPlaylistIris = playlistGroup.items.map(list => list[RESPONSE.ID]);

        const isPlaylistAlreadyAssigned = existedPlaylistIris.includes(playlistIri);

        if (isPlaylistAlreadyAssigned) {
            // @ts-ignore
            const deleteItemsId = playlistGroup.items.find(item => item[RESPONSE.ID] === playlistIri)['itemId'];
            const url = `${Server.baseUrl}${deleteItemsId}`;
            await apiDelete(url)
        } else {
            const postItemsUrl = `${Server.baseUrl}${playlistGroup[RESPONSE.ID]}/items`;
            const options = {
                body: {
                    playlist: playlistIri,
                    position: 0,
                }
            };
            await apiPost(postItemsUrl, options);
        }

        await getPlaylistGroup(playlistGroup.uuid, {
            isBackgroundRequest: true
        });
        setIsSaving(false);
    }

    const getPlaylistGroupCountry = (playlistGroup: PlaylistGroup): string | undefined => {
        return (allCountries.find(c => c["@id"] === playlistGroup.country) as Country)?.name;
    }

    const handleDeletePlaylistGroupClick = () => {
        setIsDeletingPlaylistGroup(true);
        const url = `${Server.baseUrl}${playlistGroup[RESPONSE.ID]}`;
        apiDelete(url).then(() => {
            setTimeout(() => {
                setIsDeletingPlaylistGroup(false);
                setIsDeleteModalOpen(false);
                    navigate(ROUTES.PLAYLISTS);
            }, 1500);
        });
    };

    const handleSetDevices = (deviceIris) => {
        setDevicesToSet(deviceIris);
    };

    const handleSaveDevices = async () => {
        setIsSaving(true);

        const putPromises: Promise<Device>[] = [];
        // @ts-ignore
        const groupDevices = devicesPersisted.map(getIri);

        const devicesToAdd = devicesToSet.filter(iri => !groupDevices.includes(iri));
        // @ts-ignore
        const devicesToRemove = groupDevices.filter(device => !devicesToSet.includes(device));

        for (let i = 0; i < devicesToAdd.length; i++) {
            const url = `${Server.baseUrl}${devicesToAdd[i]}`;
            const options = {
                body: {
                    playlistGroups: [playlistGroup[RESPONSE.ID]],
                }
            };
            putPromises.push(apiPatch<ResourceResponse<Device>>(url, options));
        }

        for (let i = 0; i < devicesToRemove.length; i++) {
            const url = `${Server.baseUrl}${devicesToRemove[i]}`;
            const options = {
                body: {
                    playlistGroups: [],
                }
            };
            putPromises.push(apiPatch<ResourceResponse<Device>>(url, options));
        }

        Promise.all(putPromises).then(async () => {
            refreshCoreData({ isBackgroundRequest: true });
        });

        setTimeout(async() => {
            await getPlaylistGroup(playlistGroup.uuid, {
                isBackgroundRequest: true
            });
            setIsSaving(false);
            setIsDeviceModalOpen(false);
        }, 1500);
    };

    const handleCancelEditingDevices = () => {
        setIsDeviceModalOpen(false);
        setDevicesPersisted(devicesPersisted);
    };

    const handleRemoveDevice = async (deviceId: string) => {
        setIsFetchingDevicesInfo(true);

        const url = `${Server.baseUrl}${deviceId}`;

        const options = {
            body: {
                playlistGroups: [],
            }
        };

        await apiPatch<ResourceResponse<Device>>(url, options);

        await getPlaylistGroup(playlistGroupId, {
            isBackgroundRequest: true
        });

        setIsFetchingDevicesInfo(false);
    }

    const getPlaylistGroupDevicesInfo = async () => {
        if (isFetchingDevicesInfo) {
            controllerRef.current?.abort();
        }

        setIsFetchingDevicesInfo(true);

        controllerRef.current = new AbortController();
        const signal = controllerRef.current?.signal;
        try {
            // first get all user dealerships
            let nextDealerships: any = [];

            const params = {
                brand: details.userGrantedBrands,
                hasDevices: true,
                pageSize: 300
            };

            const result = await api.user.getAllUserDealerships({
                params,
                signal
            });

            nextDealerships = result;

            // second get the shared devices between user and playlist
            const playlistUserDevices = nextDealerships.reduce(
                (accumulatedDevices: string[], dealership: Dealership) => {
                    return accumulatedDevices.concat([...dealership.devices]);
                },
                []
            );

            const playlistGroupDevices = playlistGroup.devices;

            playlistGroupDevices.forEach(device =>
                playlistUserDevices.includes(device[RESPONSE.ID]) ? device.isUserDevice = true : device.isUserDevice = false
            )

            setShowDevicesHint(playlistGroupDevices.some(device => !device.isUserDevice));
            setDevicesPersisted(playlistGroupDevices);
            setDevicesToSet(playlistGroupDevices);
            setIsFetchingDevicesInfo(false);
        } catch (e) {
            setIsFetchingDevicesInfo(false);
            return '';
        }
    };

    const handleGoToDevice = (device: Device) => {
        navigate(`${ROUTES.DEALERSHIPS}${ROUTES.DEVICES}/${device[RESPONSE.ID].split('/').pop()}?brand=${device[RESPONSE.ID].split('/')[2]}`);
    };

    const classifyDevicesByDealership = (devices) => {
        const uniqueDevices = devices.map(device => ({dealer: device.dealership?.split('/').pop(), device: device}));

        const grouped = uniqueDevices.reduce((acc, {dealer, device}) => {
            acc[dealer] = acc[dealer] || [];
            acc[dealer].push(device);
            return acc;
        }, {});

        return Object.entries(grouped).map(([dealer, devices]) => ({
            dealer,
            devices
        }));

    };

    const renderDevices =
        (viewableDevices: [] | Device[]) => {
            if (isNotEmpty(viewableDevices)) {
                return (
                    <>
                        {
                            classifyDevicesByDealership(viewableDevices).map(dealer =>
                                // @ts-ignore
                                <Accordion key={dealer.dealer} title={`${dealer.dealer} (${dealer.devices.length}  ${t('playlist_device')})`}>
                                    {(dealer.devices as Device[]).map(device => (
                                        <div className={cx('device-list-item', { 'is-disabled': !device.isUserDevice })} 
                                            key={device[RESPONSE.ID]}>
                                            <p
                                                onClick={() => handleGoToDevice(device)}
                                            >
                                                {`${device[RESPONSE.ID].split('/').pop()} - ${
                                                    device.title
                                                }`}
                                            </p>
                                            {device.isUserDevice && <button
                                                className="remove-device-button"
                                                onClick={() => handleRemoveDevice(device[RESPONSE.ID])}
                                            >
                                                X
                                            </button>}
                                        </div>
                                    ))}
                                </Accordion>
                            )
                        }
                    </>
                );
            }

            return <span className="no-data">{t('playlist_no_devices')}</span>;
        };

    const handleSuccessfulPlaylistGroupEditing = () => {
        getPlaylistGroup(playlistGroupId);
        setIsEditingPlaylistGroup(false);
    };

    const {
        description,
        title,
        loading,
    } = playlistGroupInfo;

    useEffect(() => {
        return () => {
            setPageName('');
            controllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        setPageName(title);
        setPlaylistGroupInfo(playlistGroup);
        getPlaylistGroupDevicesInfo()
    }, [playlistGroup]);

    useEffect(() => {
        setIsEditable(details.brandResponsible || details.marketResponsible || playlistGroup.createdBy === details[RESPONSE.ID]);
    }, [details]);

    return (
        <div className="b-playlistGroupContent">
            <div className='back-button-wrapper'>
                <GoBackButton/>
                <div className='playlist-group-actions'>
                    <button
                        className={cx('b-playlist-items-list__assign', {})}
                        onClick={handleModifyDevices}
                        data-testid='assign-devices'
                    >
                        <IconButton src={assignIconPath}/>
                        {t('playlist_assign')}
                    </button>
                    <button
                        className={cx('b-playlist-items-list__duplicate', {})}
                        onClick={() => {setIsDuplicateModalOpen(true);}}
                        data-testid='duplicate-playlist'
                    >
                        <IconButton src={duplicateIconPath} />
                        {t('playlist_duplicate')}
                    </button>
                    <button
                        className={cx('b-playlist-items-list__delete-button', {
                            disabled: !isEditable
                        })}
                        onClick={handleDeleteModalOpen}
                        data-testid='delete-playlist-group'
                    >
                        <IconButton src={deleteIconPath}/>
                        {t('playlist_group_delete')}
                    </button>
                </div>
            </div>
            <div className='playlist-group-header'>
                <h3>{title}</h3>
                <IconButton onClick={handleEditPlaylistGroup} icon="edit"/>
            </div>
            <div className="playlist-group-info">
                {isEditingPlaylistGroup ? (
                    <EditPlaylistGroup
                        user={details}
                        playlistGroup={playlistGroup}
                        onCancel={handleCloseEditModal}
                        onSuccess={handleSuccessfulPlaylistGroupEditing}
                    />
                ) : (
                    <>
                        <div className='info'>
                            <h4>{t('title')}</h4>
                            <p>{title}</p>
                        </div>
                        <div className='info'>
                            <h4>{t('target_market')}</h4>
                            <p>{getPlaylistGroupCountry(playlistGroup)}</p>
                        </div>
                        <div className='info'>
                            <h4>{t('playlist_content')}</h4>
                            <p
                                className={` ${
                                    description === '' ? 'no-data' : ''
                                }`}
                            >
                                {description !== ''
                                    ? description
                                    : t('playlist_no_desc')}
                            </p>
                        </div>
                    </>
                )}
            </div>
            <div className='playlist-group-devices-header'>
                <h3>{t('playlist_device')}</h3>
                <p className='devices-count' data-testid='playlist-group-devices-count'>{`${devicesPersisted.length} ${t('playlist_device')}`}</p>
            </div>
            <div className='playlist-group-devices'>
                {devicesPersisted.length > 0 && showDevicesHint && <p>* {t('playlist_group_devices_hint')}</p>}
                <div className="devices">
                    {isFetchingDevicesInfo ? (
                        <Loader isInline isSmall/>
                    ) : (
                        renderDevices(devicesPersisted)
                    )}
                </div>
            </div>
            <div className='playlist-group-elements-header'>
                <h3>{t('playlists')}</h3>
                <IconButton
                    onClick={handleOpenAddPlaylistModal}
                    icon="system-add-large"
                />
            </div>
            <div className='playlist-group-elements'>
                {loading
                ? <Loader/>
                // @ts-ignore
                : <PlaylistGroupPlylists items={playlistGroup.items} iri={playlistGroupId}/>}
            </div>
            {isDeviceModalOpen && (
                <DeviceAssignmentModal
                    isForPlaylistGroup={true}
                    playlist={playlistGroup}
                    isSaving={isSaving}
                    onClose={handleCancelEditingDevices}
                    onCancel={handleCancelEditingDevices}
                    onAction={handleSaveDevices}
                    onSelect={handleSetDevices}
                    defaultValue={devicesPersisted}
                />
            )}
            <DeleteConfirmationModal
                show={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onCancel={handleCloseDeleteModal}
                onAction={handleDeletePlaylistGroupClick}
                isLoading={isDeletingPlaylistGroup}
            >
                <h4>{t('delete_playlist_group_confirm')}</h4>
            </DeleteConfirmationModal>
            <Modal
                show={isAddPlaylistModalOpen}
                position="left"
                onClose={handleCloseAddPlaylistModal}
            >
                <Modal.Title>
                    {t('playlist_group_add_items_modal_title')}
                    <div className="button-container">
                        <Button
                            clickHandler={() => setSelectedSource('my_playlists')}
                            text={t('my_playlists')}
                            secondaryButton={selectedSource !== 'my_playlists'}
                        />
                        <Button
                            clickHandler={() => setSelectedSource('market_playlists')}
                            text={t('market_playlists')}
                            secondaryButton={selectedSource !== 'market_playlists'}
                        />
                    </div>
                </Modal.Title>
                <Modal.Content>
                    {selectedSource === 'my_playlists' && <AssignPlaylistToPlaylistGroupOverlay
                        onItemClick={onPlaylistAssignment}
                        isFilterable
                        playlistGroup={playlistGroup}
                    />}
                    {selectedSource === 'market_playlists' && <AssignPlaylistToPlaylistGroupOverlay
                        onItemClick={onPlaylistAssignment}
                        isFilterable
                        playlistGroup={playlistGroup}
                        market
                    />}
                </Modal.Content>
            </Modal>
            <CreatePlaylistGroupModal
                onClose={() => setIsDuplicateModalOpen(false)}
                isOpen={isDuplicateModalOpen}
                primaryButtonText={'Duplicate'}
                onSuccess={() => setIsAddPlaylistModalOpen(false)}
                playlistGroup={playlistGroup}
            />
        </div>
    );
};

export default PlaylistGroupContent;
